import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Download, FileText, Receipt, RefreshCw } from 'lucide-react';
import { useTheme } from '../../../../context/ThemeContext';
import { descargarPDFFactura, listarMisFacturas } from '../../../../services/facturacionService';

import { Pagination } from '../../../../components/paginationlanding/Pagination';

type EstadoFactura = 'validada' | 'anulada';

interface FacturaCliente {
    id: number;
    numero_factura: number;
    estado: EstadoFactura;
    fecha_emision?: string | null;
    empresa_nombre?: string;
    cita_codigo?: string;
    venta_codigo?: string;
    total?: string | number;
}

const colores = {
    primary: '#61257B',
    primaryDark: '#451759',
    secondary: '#C1348B',
    accent: '#EA8406',
};

const esFacturaDisponible = (factura: any): factura is FacturaCliente =>
    Boolean(
        factura
        && factura.id
        && factura.numero_factura
        && ['validada', 'anulada'].includes(factura.estado),
    );

const formatearFecha = (fecha?: string | null) => {
    if (!fecha) return 'Fecha no disponible';
    return new Intl.DateTimeFormat('es-BO', {
        day: '2-digit', month: 'long', year: 'numeric',
    }).format(new Date(fecha));
};

const formatearTotal = (total?: string | number) => {
    if (total === undefined || total === null || total === '') return null;
    const valor = Number(total);
    return Number.isFinite(valor)
        ? new Intl.NumberFormat('es-BO', { style: 'currency', currency: 'BOB' }).format(valor)
        : null;
};

export default function MisFacturasClientePage() {
    const { isDark } = useTheme();
    const [pagina, setPagina] = useState(1);
    const [total, setTotal] = useState(0);
    const cargaVersion = useRef(0);
    const [facturas, setFacturas] = useState<FacturaCliente[]>([]);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState('');
    const [falloCarga, setFalloCarga] = useState(false);
    const [descargando, setDescargando] = useState<string | null>(null);

    const cargar = useCallback(async () => {
        const version = ++cargaVersion.current;
        setCargando(true);
        setError('');
        setFalloCarga(false);
        try {
            const respuesta = await listarMisFacturas(pagina);
            if (version !== cargaVersion.current) return;
            setFacturas(respuesta.results.filter(esFacturaDisponible));
            setTotal(respuesta.count);
        } catch (e: any) {
            if (version !== cargaVersion.current) return;
            setFacturas([]);
            setFalloCarga(true);
            setError(e?.message || 'No pudimos cargar tus facturas. Intenta nuevamente.');
        } finally {
            if (version === cargaVersion.current) setCargando(false);
        }
    }, [pagina]);

    useEffect(() => { void cargar(); return () => { cargaVersion.current++; }; }, [cargar]);

    const descargar = async (factura: FacturaCliente, formato: 'carta' | 'rollo') => {
        const clave = `${factura.id}-${formato}`;
        if (descargando) return;
        setDescargando(clave);
        setError('');
        try {
            await descargarPDFFactura(factura.id, formato);
        } catch (e: any) {
            setError(e?.message || 'No se pudo descargar la factura. Intenta nuevamente.');
        } finally {
            setDescargando(null);
        }
    };

    const texto = isDark ? '#f8fafc' : '#0f172a';
    const secundario = isDark ? '#cbd5e1' : '#64748b';
    const tarjeta = isDark ? 'rgba(22, 10, 43, 0.88)' : '#ffffff';

    return (
        <main style={{ minHeight: '100vh', padding: 'clamp(24px, 4vw, 44px) clamp(16px, 4vw, 32px)', fontFamily: "'DM Sans', sans-serif" }}>
            <div style={{ maxWidth: 1100, margin: '0 auto' }}>
                <div style={{ marginBottom: 30 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: colores.primary, fontSize: 13, fontWeight: 800, marginBottom: 10 }}>
                        <Receipt size={16} aria-hidden="true" /> Mis Facturas
                    </div>
                    <h1 style={{ margin: 0, color: texto, fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', fontWeight: 900 }}>
                        Mis <span style={{ color: isDark ? '#FFD97C' : colores.primary }}>Facturas</span>
                    </h1>
                    <p style={{ color: secundario, marginTop: 8 }}>
                        Consulta las facturas de tus reservas y compras de cafetería y descarga una copia en PDF.
                    </p>
                </div>

                {error && (
                    <div role="alert" style={{ marginBottom: 18, padding: '13px 16px', borderRadius: 12, color: isDark ? '#fecdd3' : '#9f1239', background: isDark ? 'rgba(190,24,93,.18)' : '#fff1f2' }}>
                        {error}
                        {falloCarga && (
                            <button type="button" onClick={() => void cargar()} aria-label="Reintentar carga de facturas" style={{ marginLeft: 12, padding: '7px 11px', borderRadius: 8, border: '1px solid currentColor', background: 'transparent', color: 'inherit', fontWeight: 800, cursor: 'pointer' }}>
                                Reintentar
                            </button>
                        )}
                    </div>
                )}

                {cargando ? (
                    <div aria-label="Cargando facturas" style={{ color: secundario, padding: '60px 0', textAlign: 'center' }}>
                        <RefreshCw size={30} style={{ margin: '0 auto 12px' }} aria-hidden="true" />
                        Cargando tus facturas…
                    </div>
                ) : !falloCarga && facturas.length === 0 ? (
                    <section style={{ padding: '52px 24px', textAlign: 'center', borderRadius: 22, background: tarjeta, border: `1px solid ${isDark ? 'rgba(193,52,139,.25)' : '#e2e8f0'}` }}>
                        <FileText size={38} color={isDark ? '#FFD97C' : colores.primary} style={{ margin: '0 auto 14px' }} aria-hidden="true" />
                        <h2 style={{ color: texto, fontSize: 18, marginBottom: 7 }}>Aún no tienes facturas emitidas</h2>
                        <p style={{ color: secundario, margin: 0 }}>Cuando una reserva o compra sea facturada, aparecerá aquí automáticamente.</p>
                    </section>
                ) : facturas.length > 0 ? (
                    <section aria-label="Listado de facturas" style={{ display: 'grid', gap: 14 }}>
                        {facturas.map(factura => {
                            const anulada = factura.estado === 'anulada';
                            const total = formatearTotal(factura.total);
                            return (
                                <article key={factura.id} style={{ padding: '20px clamp(16px, 3vw, 24px)', borderRadius: 18, background: tarjeta, border: `1px solid ${anulada ? 'rgba(190,24,93,.35)' : isDark ? 'rgba(193,52,139,.25)' : '#e2e8f0'}`, boxShadow: isDark ? '0 12px 30px rgba(0,0,0,.22)' : '0 8px 24px rgba(15,23,42,.06)' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                                <h2 style={{ margin: 0, color: texto, fontSize: 17, fontWeight: 900 }}>Factura N° {factura.numero_factura}</h2>
                                                <span style={{ padding: '4px 9px', borderRadius: 999, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: anulada ? '#be123c' : '#166534', background: anulada ? '#fff1f2' : '#dcfce7' }}>
                                                    {anulada ? 'Anulada' : 'Validada'}
                                                </span>
                                            </div>
                                            <p style={{ color: secundario, margin: '7px 0 0', fontSize: 13 }}>
                                                {factura.empresa_nombre || 'Co Marca'} · {factura.venta_codigo || factura.cita_codigo || 'Reserva'} · {formatearFecha(factura.fecha_emision)}
                                                {total ? ` · ${total}` : ''}
                                            </p>
                                        </div>
                                        <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
                                            {(['carta', 'rollo'] as const).map(formato => {
                                                const clave = `${factura.id}-${formato}`;
                                                return (
                                                    <button key={formato} type="button" onClick={() => void descargar(factura, formato)} disabled={Boolean(descargando)} aria-label={`Descargar ${formato} de factura ${factura.numero_factura}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '10px 13px', borderRadius: 10, border: `1px solid ${colores.primary}`, background: formato === 'carta' ? `linear-gradient(135deg, ${colores.primaryDark}, ${colores.primary})` : 'transparent', color: formato === 'carta' || isDark ? '#fff' : colores.primary, fontWeight: 800, cursor: descargando ? 'wait' : 'pointer', opacity: descargando ? .65 : 1 }}>
                                                        <Download size={15} aria-hidden="true" />
                                                        {descargando === clave ? 'Descargando…' : formato === 'carta' ? 'Descargar carta' : 'Descargar rollo'}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                    </section>
                ) : null}
                {!falloCarga && <Pagination currentPage={pagina} totalPages={Math.ceil(total / 10)} onPageChange={setPagina} disabled={cargando} />}
            </div>
        </main>
    );
}


